import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
import { Badge } from './badge';
import { toast } from 'sonner';
import {
  Plus, Check, X, Minus, Hand, Clock, Users, MessageCircle,
  ChevronDown, ChevronRight, RefreshCw, Lightbulb, BarChart3,
  ThumbsUp, ThumbsDown, Circle, AlertTriangle, Calendar,
  Archive, Search, Filter, Zap, Target, Heart
} from 'lucide-react';
import { ConsentFlowIcon } from './legendary-icons';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Loomio-inspired position types with clear meanings
const POSITION_TYPES = [
  { 
    key: 'agree', 
    label: 'Agree', 
    icon: ThumbsUp, 
    color: '#2f6f3e',
    bg: '#2f6f3e15',
    description: 'I support this proposal',
    weight: 1
  },
  { 
    key: 'abstain', 
    label: 'Abstain', 
    icon: Minus, 
    color: '#6b7280',
    bg: '#6b728015',
    description: 'I have no strong opinion',
    weight: 0
  },
  { 
    key: 'disagree', 
    label: 'Disagree', 
    icon: ThumbsDown, 
    color: '#b9935a',
    bg: '#b9935a15',
    description: 'I have concerns but won\'t block',
    weight: -1
  },
  { 
    key: 'block', 
    label: 'Block', 
    icon: Hand, 
    color: '#9d3b3b',
    bg: '#9d3b3b15',
    description: 'This would cause harm - needs discussion',
    weight: -2,
    requiresReason: true
  }
];

// Decision types for different situations
const DECISION_TYPES = [
  {
    key: 'consent',
    name: 'Consent',
    icon: Check,
    color: '#2f6f3e',
    description: 'Safe to try? No blocks means we proceed.',
    threshold: 'No blocks required'
  },
  {
    key: 'consensus',
    name: 'Consensus',
    icon: Users,
    color: '#4a7c59',
    description: 'Everyone agrees or abstains.',
    threshold: 'All agree or abstain'
  },
  {
    key: 'advice',
    name: 'Advice',
    icon: MessageCircle,
    color: '#b9935a',
    description: 'Gather input, then decide.',
    threshold: 'Input collected'
  },
  {
    key: 'poll',
    name: 'Poll',
    icon: BarChart3,
    color: '#6366f1',
    description: 'Majority or plurality wins.',
    threshold: 'Most votes'
  }
];

// Quick templates
const PROPOSAL_TEMPLATES = [
  { title: 'Resource Sharing', category: 'Resources', icon: '🌿' },
  { title: 'Meeting Rhythm', category: 'Culture', icon: '🔄' },
  { title: 'New Member Welcome', category: 'Membership', icon: '👋' },
  { title: 'Conflict Process', category: 'Governance', icon: '🤝' },
  { title: 'Budget Allocation', category: 'Resources', icon: '💰' },
  { title: 'Role Definition', category: 'Structure', icon: '🎯' }
];

// Visual pie chart component for positions
const PositionPieChart = ({ positions, size = 80 }) => {
  const total = positions.length || 1;
  const counts = POSITION_TYPES.reduce((acc, type) => {
    acc[type.key] = positions.filter(p => p.position === type.key).length;
    return acc;
  }, {});
  
  let currentAngle = -90; // Start from top
  
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="45" fill="var(--muted)" opacity="0.3" />
      {POSITION_TYPES.map((type) => {
        const count = counts[type.key] || 0;
        if (count === 0) return null;
        
        const percentage = count / total;
        const angle = percentage * 360;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;
        currentAngle = endAngle;
        
        // Calculate arc path
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const x1 = 50 + 45 * Math.cos(startRad);
        const y1 = 50 + 45 * Math.sin(startRad);
        const x2 = 50 + 45 * Math.cos(endRad);
        const y2 = 50 + 45 * Math.sin(endRad);
        const largeArc = angle > 180 ? 1 : 0;
        
        return (
          <path
            key={type.key}
            d={`M 50 50 L ${x1} ${y1} A 45 45 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={type.color}
            opacity="0.85"
          />
        );
      })}
      <circle cx="50" cy="50" r="25" fill="var(--card)" />
      <text x="50" y="55" textAnchor="middle" fontSize="18" fontWeight="bold" fill="var(--fg)">
        {total}
      </text>
    </svg>
  );
};

// Position bar visualization
const PositionBar = ({ positions }) => {
  const total = positions.length || 1;
  
  return (
    <div className="flex h-3 rounded-full overflow-hidden bg-[var(--muted)]/30">
      {POSITION_TYPES.map((type) => {
        const count = positions.filter(p => p.position === type.key).length;
        if (count === 0) return null;
        const percentage = (count / total) * 100;
        
        return (
          <motion.div
            key={type.key}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{ backgroundColor: type.color }}
            title={`${type.label}: ${count}`}
          />
        );
      })}
    </div>
  );
};

// Countdown timer component
const TimeRemaining = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const end = new Date(deadline);
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft('Closed');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h left`);
      } else if (hours > 0) {
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeLeft(`${hours}h ${mins}m left`);
      } else {
        const mins = Math.floor(diff / (1000 * 60));
        setTimeLeft(`${mins}m left`);
      }
    };
    
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [deadline]);
  
  const isUrgent = timeLeft.includes('h') && !timeLeft.includes('d');
  
  return (
    <span className={`flex items-center gap-1 text-xs ${isUrgent ? 'text-amber-600' : 'text-[var(--fg)]/50'}`}>
      <Clock size={12} />
      {timeLeft}
    </span>
  );
};

export const ConsentFlow = ({ projectId }) => {
  const [decisions, setDecisions] = useState([]);
  const [showNewProposal, setShowNewProposal] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('active');
  const [showTemplates, setShowTemplates] = useState(false);
  const [discussionInput, setDiscussionInput] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [showBlockInput, setShowBlockInput] = useState(false);
  
  const [newProposal, setNewProposal] = useState({
    title: '',
    context: '',
    proposal: '',
    decision_type: 'consent',
    deadline_days: 3
  });

  const userName = localStorage.getItem('blissabode_user_name') || 'Anonymous';

  useEffect(() => {
    if (projectId) {
      loadDecisions();
    }
  }, [projectId]);

  const loadDecisions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/decisions`);
      if (response.ok) {
        const data = await response.json();
        setDecisions(data.decisions || []);
      }
    } catch (error) {
      console.error('Failed to load decisions:', error);
    }
  };

  const applyTemplate = (template) => {
    setNewProposal({
      ...newProposal,
      title: template.title,
      context: `This proposal is about our ${template.category.toLowerCase()} practices.`
    });
    setShowTemplates(false);
  };

  const createProposal = async () => {
    if (!newProposal.title || !newProposal.proposal) {
      toast.error('Please add a title and proposal');
      return;
    }

    setIsLoading(true);
    
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + newProposal.deadline_days);
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/decisions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newProposal,
          deadline: deadline.toISOString(),
          author: userName,
          positions: [],
          discussion: []
        })
      });

      if (response.ok) {
        const data = await response.json();
        setDecisions([data, ...decisions]);
        setShowNewProposal(false);
        setNewProposal({
          title: '', context: '', proposal: '',
          decision_type: 'consent', deadline_days: 3
        });
        toast.success('Proposal created! Share with your team.');
      }
    } catch (error) {
      toast.error('Failed to create proposal');
    } finally {
      setIsLoading(false);
    }
  };

  const submitPosition = async (decisionId, positionKey, reason = '') => {
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/decisions/${decisionId}/check`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            decision_id: decisionId,
            user_name: userName,
            response_type: positionKey,
            content: reason
          })
        }
      );

      if (response.ok) {
        const updated = await response.json();
        setDecisions(decisions.map(d => d.decision_id === decisionId ? updated : d));
        setSelectedDecision(updated);
        setShowBlockInput(false);
        setBlockReason('');
        
        const posType = POSITION_TYPES.find(p => p.key === positionKey);
        toast.success(`Position recorded: ${posType?.label}`);
      }
    } catch (error) {
      toast.error('Failed to record position');
    }
  };

  const addDiscussion = async (decisionId) => {
    if (!discussionInput.trim()) return;
    
    // For now, add to local state (backend would handle this)
    const updated = decisions.map(d => {
      if (d.decision_id === decisionId) {
        return {
          ...d,
          discussion: [...(d.discussion || []), {
            author: userName,
            content: discussionInput,
            timestamp: new Date().toISOString()
          }]
        };
      }
      return d;
    });
    setDecisions(updated);
    setSelectedDecision(updated.find(d => d.decision_id === decisionId));
    setDiscussionInput('');
    toast.success('Comment added');
  };

  const getUserPosition = (decision) => {
    const positions = decision.consent_checks || decision.positions || [];
    return positions.find(p => p.user_name === userName);
  };

  const getPositionSummary = (decision) => {
    const positions = decision.consent_checks || decision.positions || [];
    return {
      total: positions.length,
      agree: positions.filter(p => p.response_type === 'agree' || p.response_type === 'consent').length,
      abstain: positions.filter(p => p.response_type === 'abstain').length,
      disagree: positions.filter(p => p.response_type === 'disagree' || p.response_type === 'clarifying_question').length,
      block: positions.filter(p => p.response_type === 'block' || p.response_type === 'objection').length
    };
  };

  const getOutcomeStatus = (decision) => {
    const summary = getPositionSummary(decision);
    const decisionType = decision.decision_type || 'consent';
    
    if (summary.total === 0) return { status: 'waiting', label: 'Awaiting Input', color: '#6b7280' };
    
    if (decisionType === 'consent') {
      if (summary.block > 0) return { status: 'blocked', label: 'Blocked - Needs Discussion', color: '#9d3b3b' };
      if (summary.agree > 0) return { status: 'passing', label: 'Safe to Try', color: '#2f6f3e' };
    }
    
    if (decisionType === 'consensus') {
      if (summary.disagree > 0 || summary.block > 0) return { status: 'concerns', label: 'Has Concerns', color: '#b9935a' };
      return { status: 'passing', label: 'Consensus Forming', color: '#2f6f3e' };
    }
    
    return { status: 'active', label: 'In Progress', color: '#6366f1' };
  };

  const filteredDecisions = decisions.filter(d => {
    if (filterStatus === 'active') return d.status !== 'resolved' && d.status !== 'archived';
    if (filterStatus === 'resolved') return d.status === 'resolved';
    if (filterStatus === 'archived') return d.status === 'archived';
    return true;
  });

  return (
    <div className="p-4 h-full overflow-y-auto" data-testid="consent-flow">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ConsentFlowIcon size={20} className="text-[var(--accent)]" />
          <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
            Decisions
          </h4>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowNewProposal(true)}
          className="text-xs"
          data-testid="new-proposal-btn"
        >
          <Plus size={14} className="mr-1" />
          Propose
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 p-1 bg-[var(--muted)]/20 rounded-lg mb-4">
        {[
          { key: 'active', label: 'Active' },
          { key: 'resolved', label: 'Resolved' },
          { key: 'all', label: 'All' }
        ].map((filter) => (
          <button
            key={filter.key}
            onClick={() => setFilterStatus(filter.key)}
            className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${
              filterStatus === filter.key
                ? 'bg-[var(--card)] text-[var(--fg)] shadow-sm'
                : 'text-[var(--fg)]/50 hover:text-[var(--fg)]/70'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Loomio-inspired principle */}
      <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-[var(--primary)]/10 to-[var(--accent)]/10 border border-[var(--primary)]/20">
        <div className="flex items-start gap-2">
          <Zap size={14} className="text-[var(--primary)] mt-0.5" />
          <div>
            <p className="text-xs text-[var(--fg)]/70">
              <span className="font-medium text-[var(--primary)]">Collaborative decisions:</span> See where everyone stands. 
              Blocks require discussion. Disagreement is welcome — it makes decisions stronger.
            </p>
          </div>
        </div>
      </div>

      {/* New Proposal Form */}
      <AnimatePresence>
        {showNewProposal && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-4 overflow-hidden"
          >
            <Card className="border-[var(--primary)]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">New Proposal</CardTitle>
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="text-xs text-[var(--primary)] hover:underline"
                  >
                    {showTemplates ? 'Hide templates' : 'Templates'}
                  </button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Templates grid */}
                <AnimatePresence>
                  {showTemplates && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="grid grid-cols-3 gap-2 mb-2"
                    >
                      {PROPOSAL_TEMPLATES.map((t) => (
                        <button
                          key={t.title}
                          onClick={() => applyTemplate(t)}
                          className="p-2 text-center text-xs rounded-lg bg-[var(--muted)]/30 hover:bg-[var(--muted)]/50 transition-colors"
                        >
                          <span className="block text-lg mb-1">{t.icon}</span>
                          <span className="text-[var(--fg)]/70">{t.title}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <Input
                  placeholder="Proposal title"
                  value={newProposal.title}
                  onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                  className="text-sm"
                />
                
                <Textarea
                  placeholder="Context: Why is this decision needed?"
                  value={newProposal.context}
                  onChange={(e) => setNewProposal({ ...newProposal, context: e.target.value })}
                  className="text-sm min-h-[60px]"
                />
                
                <Textarea
                  placeholder="Proposal: What exactly are you proposing?"
                  value={newProposal.proposal}
                  onChange={(e) => setNewProposal({ ...newProposal, proposal: e.target.value })}
                  className="text-sm min-h-[80px]"
                />

                {/* Decision type selector */}
                <div>
                  <p className="text-xs text-[var(--fg)]/50 mb-2">Decision type:</p>
                  <div className="grid grid-cols-4 gap-1">
                    {DECISION_TYPES.map((type) => (
                      <button
                        key={type.key}
                        onClick={() => setNewProposal({ ...newProposal, decision_type: type.key })}
                        className={`p-2 rounded-lg text-center transition-all ${
                          newProposal.decision_type === type.key
                            ? 'bg-[var(--primary)]/20 border-2 border-[var(--primary)]'
                            : 'bg-[var(--muted)]/20 border-2 border-transparent'
                        }`}
                      >
                        <type.icon size={16} className="mx-auto mb-1" style={{ color: type.color }} />
                        <span className="text-[10px] text-[var(--fg)]">{type.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Deadline selector */}
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-[var(--fg)]/50" />
                  <span className="text-xs text-[var(--fg)]/50">Closes in:</span>
                  <select
                    value={newProposal.deadline_days}
                    onChange={(e) => setNewProposal({ ...newProposal, deadline_days: parseInt(e.target.value) })}
                    className="text-xs bg-[var(--muted)]/30 rounded px-2 py-1 border-none"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                  </select>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowNewProposal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={createProposal}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating...' : 'Create Proposal'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decisions List */}
      <div className="space-y-3">
        {filteredDecisions.length === 0 ? (
          <div className="text-center py-8">
            <Target size={32} className="mx-auto text-[var(--muted)] mb-2" />
            <p className="text-sm text-[var(--fg)]/50">No proposals yet</p>
            <p className="text-xs text-[var(--fg)]/30 mt-1">
              Create a proposal to start making decisions together
            </p>
          </div>
        ) : (
          filteredDecisions.map((decision) => {
            const userPosition = getUserPosition(decision);
            const summary = getPositionSummary(decision);
            const outcome = getOutcomeStatus(decision);
            const positions = decision.consent_checks || decision.positions || [];
            const decisionType = DECISION_TYPES.find(t => t.key === (decision.decision_type || 'consent'));
            
            return (
              <motion.div
                key={decision.decision_id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className={`cursor-pointer transition-all ${
                    selectedDecision?.decision_id === decision.decision_id
                      ? 'border-[var(--primary)] shadow-lg'
                      : 'hover:border-[var(--primary)]/30'
                  }`}
                  onClick={() => setSelectedDecision(
                    selectedDecision?.decision_id === decision.decision_id ? null : decision
                  )}
                >
                  <CardContent className="p-4">
                    {/* Header row */}
                    <div className="flex items-start gap-3 mb-3">
                      {/* Pie chart */}
                      <div className="flex-shrink-0">
                        <PositionPieChart positions={positions.map(p => ({ position: p.response_type }))} size={50} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h5 className="font-medium text-sm text-[var(--fg)] line-clamp-1">
                            {decision.title}
                          </h5>
                          <Badge 
                            className="text-[10px] flex-shrink-0"
                            style={{ backgroundColor: outcome.color + '20', color: outcome.color }}
                          >
                            {outcome.label}
                          </Badge>
                        </div>
                        
                        <p className="text-xs text-[var(--fg)]/60 line-clamp-2 mb-2">
                          {decision.proposal}
                        </p>
                        
                        {/* Position bar */}
                        <PositionBar positions={positions.map(p => ({ position: p.response_type }))} />
                        
                        {/* Meta row */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-[var(--fg)]/50">
                          <span className="flex items-center gap-1">
                            <decisionType.icon size={12} style={{ color: decisionType.color }} />
                            {decisionType.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={12} />
                            {summary.total} voted
                          </span>
                          {decision.deadline && <TimeRemaining deadline={decision.deadline} />}
                          {userPosition && (
                            <span className="text-[var(--primary)]">✓ You voted</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded view */}
                    <AnimatePresence>
                      {selectedDecision?.decision_id === decision.decision_id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-4 pt-4 border-t border-[var(--muted)] space-y-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Context */}
                          {decision.context && (
                            <div className="p-3 rounded-lg bg-[var(--muted)]/20">
                              <p className="text-xs font-medium text-[var(--fg)]/70 mb-1">Context</p>
                              <p className="text-xs text-[var(--fg)]/60">{decision.context}</p>
                            </div>
                          )}

                          {/* Position buttons */}
                          {!userPosition && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[var(--fg)]">Your position:</p>
                              <div className="grid grid-cols-4 gap-2">
                                {POSITION_TYPES.map((type) => (
                                  <button
                                    key={type.key}
                                    onClick={() => {
                                      if (type.requiresReason) {
                                        setShowBlockInput(true);
                                      } else {
                                        submitPosition(decision.decision_id, type.key);
                                      }
                                    }}
                                    className="p-3 rounded-xl transition-all hover:scale-105 text-center"
                                    style={{ backgroundColor: type.bg }}
                                  >
                                    <type.icon size={20} className="mx-auto mb-1" style={{ color: type.color }} />
                                    <span className="text-xs font-medium" style={{ color: type.color }}>
                                      {type.label}
                                    </span>
                                  </button>
                                ))}
                              </div>
                              
                              {/* Block reason input */}
                              <AnimatePresence>
                                {showBlockInput && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="space-y-2"
                                  >
                                    <Textarea
                                      placeholder="Please explain your concern (required for blocks)..."
                                      value={blockReason}
                                      onChange={(e) => setBlockReason(e.target.value)}
                                      className="text-sm min-h-[60px]"
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setShowBlockInput(false);
                                          setBlockReason('');
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => submitPosition(decision.decision_id, 'block', blockReason)}
                                        disabled={!blockReason.trim()}
                                        style={{ backgroundColor: '#9d3b3b20', color: '#9d3b3b' }}
                                      >
                                        Submit Block
                                      </Button>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          )}

                          {/* Position summary */}
                          {positions.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[var(--fg)]">Positions ({summary.total})</p>
                              <div className="grid grid-cols-4 gap-2 text-center">
                                {POSITION_TYPES.map((type) => {
                                  const count = positions.filter(p => 
                                    p.response_type === type.key || 
                                    (type.key === 'agree' && p.response_type === 'consent') ||
                                    (type.key === 'block' && p.response_type === 'objection')
                                  ).length;
                                  return (
                                    <div 
                                      key={type.key}
                                      className="p-2 rounded-lg"
                                      style={{ backgroundColor: type.bg }}
                                    >
                                      <p className="text-lg font-bold" style={{ color: type.color }}>{count}</p>
                                      <p className="text-[10px]" style={{ color: type.color }}>{type.label}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Individual positions with reasons */}
                          {positions.filter(p => p.content).length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-[var(--fg)]">Comments</p>
                              {positions.filter(p => p.content).map((pos, idx) => {
                                const type = POSITION_TYPES.find(t => 
                                  t.key === pos.response_type ||
                                  (t.key === 'agree' && pos.response_type === 'consent') ||
                                  (t.key === 'block' && pos.response_type === 'objection')
                                ) || POSITION_TYPES[0];
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-[var(--muted)]/20">
                                    <type.icon size={14} style={{ color: type.color }} className="mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-xs font-medium text-[var(--fg)]">{pos.user_name}</span>
                                      <p className="text-xs text-[var(--fg)]/60 mt-0.5">{pos.content}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Discussion thread */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-[var(--fg)]">Discussion</p>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add to the discussion..."
                                value={discussionInput}
                                onChange={(e) => setDiscussionInput(e.target.value)}
                                className="text-sm flex-1"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') addDiscussion(decision.decision_id);
                                }}
                              />
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => addDiscussion(decision.decision_id)}
                                disabled={!discussionInput.trim()}
                              >
                                <MessageCircle size={14} />
                              </Button>
                            </div>
                            
                            {(decision.discussion || []).map((comment, idx) => (
                              <div key={idx} className="p-2 rounded-lg bg-[var(--muted)]/10 text-xs">
                                <span className="font-medium text-[var(--fg)]">{comment.author}</span>
                                <span className="text-[var(--fg)]/40 ml-2">
                                  {new Date(comment.timestamp).toLocaleDateString()}
                                </span>
                                <p className="text-[var(--fg)]/70 mt-1">{comment.content}</p>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Helper tip */}
      <div className="mt-6 p-3 rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20">
        <div className="flex items-start gap-2">
          <Heart size={14} className="text-[var(--primary)] mt-0.5" />
          <div>
            <p className="text-xs font-medium text-[var(--primary)] mb-1">Decision Wisdom</p>
            <p className="text-xs text-[var(--fg)]/60">
              A block isn't an attack — it's a gift. It means someone sees a problem you might have missed. 
              Work together to find a path forward.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentFlow;
